import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Popover from '../popover'
import PopoverMenuContent from '../popover-menu-content'
import Chip from '../chip'
import ConditionalWrapper from '../conditional-wrapper'
import Tooltip from '../tooltip'
import KeyValuePair from '../key-value-pair'

const ListItem = ({ type = "primary", iconSrc, onIconClick, optionMenuItems, onOptionMenuOpen, title, titleTooltip, className, tags, itemTypeIconSrc, itemTypeIconTooltip, username, updatedAt, renderRightSection, popoverClassName, items = [], showItemsInTooltip, popoverPlacement = 'bottom', ...props }) => {
    return (
        <div className={`bimmatch-list-item item-type-${type} ${className || ""}`} {...props}>
            {iconSrc && <img src={iconSrc} alt='icon' className='list-item-icon' onClick={onIconClick} />}
            <div className='left-section'>
                <div className='item-title-container'>
                    {typeof title === 'string' ? (
                        <h3 dir="auto" className='item-title' title={titleTooltip || title}>{title}</h3>
                    ) : (
                        title ? <div dir="auto" className='item-title' title={titleTooltip || title}>{title}</div> : <></>
                    )}
                </div>
                {tags && (
                    <div className='list-item-tags'>
                        {tags.map(tagData => <Chip key={tagData.id} name={tagData.displayName} />)}
                    </div>
                )}
            </div>
            <div className='item-right-section'>
                {itemTypeIconSrc && <ConditionalWrapper condition={!!itemTypeIconTooltip} Wrapper={<Tooltip content={itemTypeIconTooltip} />}><img src={itemTypeIconSrc} alt='icon' className='list-item-icon' /></ConditionalWrapper>}
                {username && <p className='item-username truncate-text'>{username}</p>}
                {updatedAt && <p className='item-updated-at truncate-text'>{updatedAt}</p>}
                {(showItemsInTooltip && (items.length > 0)) ? (
                    <ItemsTooltip items={items}>
                        <img src="/images/icon/info-circle.svg" alt="info-icon" />
                    </ItemsTooltip>
                ) : (
                    <ListItemItems items={items} />
                )}
                {renderRightSection && renderRightSection}
            </div>
            {optionMenuItems && (
                <div className='options'>
                    <Popover
                        content={<PopoverMenuContent items={optionMenuItems} />}
                        hideArrow={true}
                        placement={popoverPlacement}
                        className={popoverClassName}
                        align='end'
                        onClick={(isOpen) => isOpen && onOptionMenuOpen && onOptionMenuOpen()}
                    >
                        <img className="bimmatch-three-dot" src="/images/icon/threedots.svg" alt="icon" />
                    </Popover>
                </div>
            )}
        </div>
    )
}

const ITEMS_GAP = 10;

const VALUES_TOOLTIP_CLASS = 'list-item-values-tooltip';

const ListItemItems = ({ items }) => {
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(items.length);

    const itemsKey = useMemo(() => items.map((item) => item.id || item.displayName || item.label).join("|"), [items]);

    const fitItems = useCallback(() => {
        const container = containerRef.current;
        const measureEl = measureRef.current;
        if (!container || !measureEl) return;

        const available = container.clientWidth;
        const widths = Array.from(measureEl.children).map((child) => child.getBoundingClientRect().width);
        const moreWidth = widths.pop() || 0;
        const total = widths.reduce((sum, width) => sum + width, 0) + (ITEMS_GAP * Math.max(widths.length - 1, 0));

        if (total <= available) {
            setVisibleCount(widths.length);
            return;
        }

        let used = moreWidth;
        let count = 0;
        for (let index = widths.length - 1; index >= 0; index -= 1) {
            const next = used + ITEMS_GAP + widths[index];
            if (next > available) break;
            used = next;
            count += 1;
        }
        setVisibleCount(count);
    }, []);

    useLayoutEffect(() => {
        fitItems();

        const container = containerRef.current;
        if (!container || typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(fitItems);
        observer.observe(container);
        return () => observer.disconnect();
    }, [fitItems, itemsKey]);

    if (!items.length) return null;

    const splitAt = Math.max(items.length - Math.min(visibleCount, items.length), 0);
    const hiddenItems = items.slice(0, splitAt);
    const visibleItems = items.slice(splitAt);

    return (
        <div className='list-item-items' ref={containerRef}>
            <div className='list-item-items-measure' ref={measureRef} aria-hidden='true'>
                {items.map((item, index) => (
                    <div className={`item ${item.className || ""}`} key={item.id || index}>
                        <ItemContent {...item} />
                    </div>
                ))}
                <span className='item-more'>...</span>
            </div>
            {hiddenItems.length > 0 && (
                <ItemsTooltip items={hiddenItems}>
                    <span className='item-more'>...</span>
                </ItemsTooltip>
            )}
            {visibleItems.map((item, index) => <Item key={item.id || index} {...item} />)}
        </div>
    )
}

const Item = ({ displayName, tooltipEnabled = false, tooltipContent, iconSrc, onClick, label, id, className }) => {

    const handleClick = useCallback((event)=>{
        if(onClick){
            onClick(id, event)
        }
    },[onClick, id])

    return (
        <div className={`item item-${id || ""} ${className || ""}`} onClick={handleClick}>
            <ConditionalWrapper condition={tooltipEnabled} Wrapper={<Tooltip content={<ItemTooltipContent keyName={tooltipContent} value={displayName || label} />} className={VALUES_TOOLTIP_CLASS} />}>
                <ItemContent displayName={displayName} label={label} iconSrc={iconSrc} />
            </ConditionalWrapper>
        </div>
    )
}

const ItemContent = ({ displayName, label, iconSrc }) => (
    <>
        {iconSrc && <img src={iconSrc} alt='icon' className='list-item-icon' />}
        <p className='item-label truncate-text'>{displayName || label}</p>
    </>
)

const ItemTooltipContent = ({ keyName, value }) => (
    keyName ? <KeyValuePair keyName={keyName} value={value} /> : <div dir='auto'>{value}</div>
)

const ItemsTooltip = ({ items, children }) => (
    <Tooltip content={<ItemsTooltipContent items={items} />} className={VALUES_TOOLTIP_CLASS}>
        {children}
    </Tooltip>
)

const ItemsTooltipContent = ({ items }) => {
    return (
        <div className='items-tooltip-content-container'>
            {items.map((item, index) => <ItemTooltipContent key={item.id || index} keyName={item.tooltipContent || item.keyName} value={item.displayName} /> )}
        </div>
    )
}

export default ListItem